import { useState, useRef, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { Header } from './components/Header';
import { CategorySidebar } from './components/CategorySidebar';
import { CategoryPage } from './components/CategoryPage';
import { HomePage } from './components/HomePage';
import { ProductDetailPage } from './components/ProductDetailPage';
import { AboutPage } from './components/AboutPage';
import { CertificationPage } from './components/CertificationPage';
import { SizeGuidePage } from './components/SizeGuidePage';
import { CareInstructionsPage } from './components/CareInstructionsPage';
import { FAQPage } from './components/FAQPage';
import { ContactModal } from './components/ContactModal';
import { ScrollToTop } from './components/ScrollToTop';
import { Footer } from './components/Footer';
import { Button } from './components/ui/button';
import { Search, X } from 'lucide-react';
import { SearchSuggestions } from './components/SearchSuggestions';
import { Product } from './types';
import {CustomerRegistration} from "./components/CustomerRegistration";
import {WhatsAppRegister} from "./components/WhatsAppRegister";

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [localSearchQuery, setLocalSearchQuery] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<Product[]>([]);
  const [isSuggestionsLoading, setIsSuggestionsLoading] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Auto-focus search input when it opens
  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  // Close search bar when navigating to a new page
  useEffect(() => {
    setIsSearchOpen(false);
  }, [location.pathname]);

  // Debounced suggestion fetching
  useEffect(() => {
    if (localSearchQuery.length < 2) {
      setSuggestions([]);
      return;
    }

    const fetchSuggestions = async () => {
      setIsSuggestionsLoading(true);
      try {
        const response = await fetch(`/api/products/suggestions?q=${encodeURIComponent(localSearchQuery)}`);
        if (response.ok) {
          const data = await response.json();
          setSuggestions(data);
        }
      } catch (error) {
        console.error('Error fetching suggestions:', error);
      } finally {
        setIsSuggestionsLoading(false);
      }
    };

    const timeoutId = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(timeoutId);
  }, [localSearchQuery]);

  // Helper to handle search submit
  const handleSearchSubmit = (query: string) => {
    setSearchQuery(query);
    setIsSearchOpen(false); // Close bar after search
    // If we're not on Home or Category page, go Home to show results
    if (location.pathname !== '/' && !location.pathname.startsWith('/category')) {
      navigate('/');
    }
  };

  const handleCategorySelect = (categoryId: string) => {
    setSelectedCategory(categoryId);
    setIsSidebarOpen(false);
    
    // Reset search when picking a category
    setSearchQuery('');
    setLocalSearchQuery('');
    setIsSearchOpen(false);

    if (categoryId === 'all') {
      navigate('/');
    } else {
      navigate(`/category/${categoryId}`);
    }
  };

  return (
    <div className="min-h-screen bg-light-grey">
      <ScrollToTop />
      <ContactModal isOpen={isContactOpen} onOpenChange={setIsContactOpen} />
      <Header
        onMenuToggle={() => setIsSidebarOpen(!isSidebarOpen)}
        onSearchToggle={() => {
          setIsSearchOpen(!isSearchOpen);
          if (isSearchOpen) {
            setLocalSearchQuery('');
            setSearchQuery('');
          }
        }}
        onContactClick={() => setIsContactOpen(true)}
      />

      <div className="flex">
        <CategorySidebar
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          selectedCategory={selectedCategory}
          onCategorySelect={handleCategorySelect}
        />

        <main className="flex-1 bg-focused-bg min-h-[calc(100vh-6rem)]">
          {isSearchOpen && (
            <div className="p-3 sm:p-6 pb-0 animate-in slide-in-from-top-4 duration-300 relative z-[100]">
              <div className="flex items-center space-x-2 bg-white p-2 rounded-xl shadow-lg border border-brand-green/20 max-w-4xl mx-auto">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search for premium jewellery..."
                    className="w-full pl-10 pr-4 py-3 bg-light-grey/50 border-none rounded-lg focus:ring-2 focus:ring-brand-green/20 outline-none text-brand-green"
                    value={localSearchQuery}
                    onChange={(e) => setLocalSearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSearchSubmit(localSearchQuery);
                      }
                    }}
                  />
                  <SearchSuggestions 
                    suggestions={suggestions} 
                    isLoading={isSuggestionsLoading}
                    onSelect={() => {
                      setIsSearchOpen(false);
                      setLocalSearchQuery('');
                    }}
                  />
                </div>
                <Button 
                  size="icon" 
                  className="bg-brand-gold hover:bg-brand-gold/90 text-brand-green h-12 w-12 shrink-0"
                  onClick={() => handleSearchSubmit(localSearchQuery)}
                >
                  <Search className="h-5 w-5" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="text-muted-foreground hover:text-brand-green h-12 w-12 shrink-0"
                  onClick={() => {
                    setIsSearchOpen(false);
                    setLocalSearchQuery('');
                    setSearchQuery('');
                  }}
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>
          )}

          <Routes>
            <Route path="/about" element={<AboutPage />} />
            <Route path="/certification" element={<CertificationPage />} />
            <Route path="/size-guide" element={<SizeGuidePage />} />
            <Route path="/care-instructions" element={<CareInstructionsPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route index element={<HomePage searchQuery={searchQuery} />} />
            <Route path="/category/:slug" element={
              <CategoryPage searchQuery={searchQuery} />
            } />
            <Route path="/product/:slug" element={<ProductDetailPage />} />
            <Route path="/register" element={<CustomerRegistration />} />
            <Route path="/registerwa" element={<WhatsAppRegister />} />
          </Routes>
        </main>
      </div>
      <Footer onContactClick={() => setIsContactOpen(true)} />
    </div>
  );
}